import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { addDays, format } from 'date-fns';
import { map, combineLatest, Observable, of } from 'rxjs';
import { enUS, es, arSA, ptBR, fr } from 'date-fns/locale';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaRemoteConfigQuery } from '@app/akita/api/remote-config/state/remote-config.query';
import { PopsySkeletonComponent } from '../../../components/skeleton/popsy-skeleton.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { ShippingBoxIconComponent } from '../../../shared/components/icons/generated/shipping-box/shipping-box.component';

@Component({
  selector: 'popsy-shipping-estimate-card',
  templateUrl: './shipping-estimate-card.template.html',
  styleUrls: ['./shipping-estimate-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    ShippingBoxIconComponent,
    NgIf,
    PopsySkeletonComponent,
    AsyncPipe,
  ],
})
export class ShippingEstimateCardComponent {
  @Input() public country?: string | null;
  public readonly deliveryEstimate$: Observable<string>;

  constructor(
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly translateService: TranslocoService,
    private readonly remoteConfigQuery: AkitaRemoteConfigQuery
  ) {
    const countryCode = this.country || this.akitaRouterQuery.country.toUpperCase();

    if (this.akitaRouterQuery.isBrowser) {
      this.deliveryEstimate$ = combineLatest([
        this.remoteConfigQuery.selectDeliveryEstimateForCountry(countryCode),
        this.translateService.langChanges$,
      ]).pipe(
        map(([estimate]) => {
          if (estimate) {
            const parsedLocale = this.translateService.getActiveLang().toUpperCase();
            let lang = enUS;
            if (parsedLocale === 'PT') {
              lang = ptBR;
            } else if (parsedLocale === 'AR') {
              lang = arSA;
            } else if (parsedLocale === 'ES') {
              lang = es;
            } else if (parsedLocale === 'FR') {
              lang = fr;
            }
            const deliveryMinDay = addDays(new Date(), estimate.minDays);
            const deliveryMaxDay = addDays(new Date(), estimate.maxDays);
            const estimateMinText = format(deliveryMinDay, 'LLLL d', {
              locale: lang,
            });

            if (
              estimate.minDays !== estimate.maxDays &&
              deliveryMaxDay.getMonth() === deliveryMinDay.getMonth()
            ) {
              const estimateMaxDayText = format(deliveryMaxDay, 'd', {
                locale: lang,
              });
              return `${estimateMinText}-${estimateMaxDayText}`;
            } else if (
              estimate.minDays !== estimate.maxDays &&
              deliveryMaxDay.getMonth() !== deliveryMinDay.getMonth()
            ) {
              const estimateMaxDayText = format(deliveryMaxDay, 'LLLL d', {
                locale: lang,
              });
              return `${estimateMinText} - ${estimateMaxDayText}`;
            } else {
              return `${estimateMinText}`;
            }
          } else {
            return '';
          }
        })
      );
    } else {
      this.deliveryEstimate$ = of('Deliver by 12-13 October');
    }
  }
}
